/* =====================================================================
   tower/driver/src/display.js — the driver's layout and colour theme (driver handheld P1).

   Parik, 2026-10-15: 4 layouts and 10 themes (5 palettes × light/dark), all listed in shared/driver-display.json. Each driver picks their own; a field left
   unpicked (null) follows the company default (tenant settings driverApp.*), then the platform fallback.
   Layout and theme are independent.

   This module is the MODEL only — what is chosen and what applies — so any layout can bind to it. It
   paints nothing. It stamps the effective choice on <html> as data attributes (data-drv-layout,
   data-drv-day-theme, data-drv-night-theme, data-drv-theme-mode) for the shell's stylesheet, and tells
   subscribers. Deciding day or night "by the sun" belongs to the shell, which knows the clock and place.

   A layout is PRESENTATION ONLY: the same screen view-models, actions (window.drv*) and catalog strings,
   arranged differently. Nothing here can change what a driver may do or what is recorded.
   ===================================================================== */
// The ONE catalog, shared with the API (which validates against the same file): shared/driver-display.json.
import CATALOG from '../../../shared/driver-display.json';
export const LAYOUTS = CATALOG.layouts.map((x) => x.id);
export const THEMES = CATALOG.themes.map((x) => x.id);
export const MODES = CATALOG.modes.slice();
const FALLBACK = Object.assign({}, CATALOG.defaults);
export const catalog = () => CATALOG;
const FIELDS = ['layout', 'dayTheme', 'nightTheme', 'themeMode'];
const CLEAR = { layout: 'layout', dayTheme: 'daytheme', nightTheme: 'nighttheme', themeMode: 'thememode' };
const KEY = 'routingapp:driver:display:v1';
// An older id (an alias in the catalog) reads as the id it became, so a choice saved on this phone or the
// office record before the real names is not lost.
function _norm(list, v) {
  if (v == null) return null; const s = String(v).trim().toLowerCase();
  const hit = list.find((x) => x.id.toLowerCase() === s || (x.aliases || []).some((a) => a.toLowerCase() === s));
  return hit ? hit.id : null;
}
const norm = { layout: (v) => _norm(CATALOG.layouts, v), dayTheme: (v) => _norm(CATALOG.themes, v), nightTheme: (v) => _norm(CATALOG.themes, v),
  themeMode: (v) => (MODES.includes(v) ? v : null) };
const valid = { layout: (v) => !!norm.layout(v), dayTheme: (v) => !!norm.dayTheme(v), nightTheme: (v) => !!norm.nightTheme(v), themeMode: (v) => !!norm.themeMode(v) };

let _company = Object.assign({}, FALLBACK);
let _pref = { layout: null, dayTheme: null, nightTheme: null, themeMode: null };
const _subs = new Set();

function _clean(o, allowNull) {
  const out = {};
  FIELDS.forEach((f) => { const v = o ? o[f] : undefined; if (v === null && allowNull) out[f] = null; else if (valid[f](v)) out[f] = norm[f](v); });
  return out;
}

/** What is chosen, the company's defaults, and what applies (+ where each value came from). */
export function display() {
  const eff = {}, source = {};
  FIELDS.forEach((f) => { eff[f] = _pref[f] != null ? _pref[f] : _company[f]; source[f] = _pref[f] != null ? 'driver' : 'company'; });
  return { pref: Object.assign({}, _pref), company: Object.assign({}, _company), effective: eff, source,
    layouts: LAYOUTS.slice(), themes: THEMES.slice(), modes: MODES.slice() };
}

function _stamp() {
  try {
    const e = display().effective, h = document.documentElement.dataset;
    h.drvLayout = e.layout; h.drvDayTheme = e.dayTheme; h.drvNightTheme = e.nightTheme; h.drvThemeMode = e.themeMode;
  } catch (e) {}
}
function _emit(source) { _stamp(); _subs.forEach((fn) => { try { fn(display(), source); } catch (e) {} }); }
function _persist() { try { localStorage.setItem(KEY, JSON.stringify({ pref: _pref, company: _company })); } catch (e) {} }

/** The office's word, from /api/driver/me (display: { layoutPref, dayThemePref, …, companyLayout, … }). */
export function applyDisplay(d, source) {
  if (!d) return false;
  const was = JSON.stringify([_company, _pref]);
  _company = Object.assign({}, FALLBACK, _clean({ layout: d.companyLayout, dayTheme: d.companyDayTheme, nightTheme: d.companyNightTheme, themeMode: d.companyThemeMode }));
  _pref = Object.assign({ layout: null, dayTheme: null, nightTheme: null, themeMode: null },
    _clean({ layout: d.layoutPref, dayTheme: d.dayThemePref, nightTheme: d.nightThemePref, themeMode: d.themeModePref }));
  // This arrives with EVERY twelve-second poll (/api/driver/me). Telling the subscribers when nothing changed
  // rebuilt the whole handheld screen and re-opened any open sheet on every poll — the screen "moving every
  // 10 seconds" (Parik 2026-10-22). Only a real change is news.
  if (JSON.stringify([_company, _pref]) === was) return false;
  _persist(); _emit(source || 'office');
  return true;
}

/**
 * The driver's change: each field a value, or null to follow the company again; a field not named is kept.
 * Saved to this phone at once and to the office record (queued offline) when signed in. Returns false
 * for a value that is not one of the choices — nothing changes then.
 */
export function setDisplay(patch) {
  if (!patch) return false;
  const next = Object.assign({}, _pref);
  for (const f of Object.keys(patch)) {
    if (!FIELDS.includes(f)) return false;
    if (patch[f] === null) next[f] = null; else if (valid[f](patch[f])) next[f] = norm[f](patch[f]); else return false;
  }
  _pref = next; _persist(); _emit('app');
  if (typeof window.drvSaveDisplay === 'function' && window.DRIVER_SESSION && window.DRIVER_SESSION.signedIn) {
    const body = {}, clear = [];
    FIELDS.forEach((f) => { if (_pref[f] != null) body[f] = _pref[f]; else clear.push(CLEAR[f]); });
    if (clear.length) body.clear = clear;
    window.drvSaveDisplay(body);
  }
  return true;
}

export function onDisplay(fn) { _subs.add(fn); return () => _subs.delete(fn); }

// Open as this phone last knew it (offline start), corrected by /me on sign-in.
try {
  const s = JSON.parse(localStorage.getItem(KEY) || 'null');
  if (s) { _company = Object.assign({}, FALLBACK, _clean(s.company)); _pref = Object.assign(_pref, _clean(s.pref, true)); }
} catch (e) {}
_stamp();

Object.assign(window, { drvDisplay: display, drvSetDisplay: setDisplay, drvApplyDisplay: applyDisplay, drvOnDisplay: onDisplay });
